import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InfoIcon from "@mui/icons-material/Info";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { removeSampleTestArray } from "../../actions/cleanup";
import {
  buildRetailItemNumberOptions,
  getDefaultRetailItemNumber,
} from "../../../constants/DefaultRetailItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";

import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import {
  isSampleSpaServiceGroupRow,
  SPA_SERVICES_LINKED_ITEM_PLACEHOLDER,
} from "../../../constants/SpaServicesFields";

const YES_NO = ["Yes", "No"];

const parseLinkedRetailItemFromOption = (
  option: string | null,
): string | null => {
  if (!option) {
    return null;
  }
  const separator = option.indexOf(" - ");
  return (separator >= 0 ? option.slice(0, separator) : option).trim() || null;
};

const linkedRetailItemToOptionValue = (
  itemNumber: string | null,
  options: string[],
): string | null => {
  if (!itemNumber) {
    return null;
  }
  const match = options.find(
    (o) => o === itemNumber || o.startsWith(`${itemNumber} - `),
  );
  return match ?? itemNumber;
};

const SpaDayPackages = ({
  data,
  fieldValues,
  spaRetailItems,
}: {
  data: (value: any) => void;
  fieldValues: any;
  spaRetailItems?: any[] | null;
}) => {
  const [packageCode, setPackageCode] = useState<string | null>(null);
  const [packageCodeFieldError, setPackageCodeFieldError] =
    useState<boolean>(false);
  const [packageCodeFieldErrorMessage, setPackageCodeFieldErrorMessage] =
    useState<string>("");
  const [packageDescription, setPackageDescription] = useState<string | null>(
    null,
  );
  const [packageDescriptionFieldError, setPackageDescriptionFieldError] =
    useState<boolean>(false);
  const [
    packageDescriptionFieldErrorMessage,
    setPackageDescriptionFieldErrorMessage,
  ] = useState<string>("");
  const [effectiveFromDate, setEffectiveFromDate] = useState<Dayjs | null>(
    null,
  );
  const [effectiveToDate, setEffectiveToDate] = useState<Dayjs | null>(null);
  const [price, setPrice] = useState<number | "">("");
  const [availableSun, setAvailableSun] = useState<string>("Yes");
  const [availableMon, setAvailableMon] = useState<string>("Yes");
  const [availableTue, setAvailableTue] = useState<string>("Yes");
  const [availableWed, setAvailableWed] = useState<string>("Yes");
  const [availableThu, setAvailableThu] = useState<string>("Yes");
  const [availableFri, setAvailableFri] = useState<string>("Yes");
  const [availableSat, setAvailableSat] = useState<string>("Yes");
  const [minimumGuests, setMinimumGuests] = useState<number | "">("");
  const [maximumGuests, setMaximumGuests] = useState<number | "">("");
  const [points, setPoints] = useState<number | "">("");
  const [active, setActive] = useState<string>("Yes");
  const [linkedItem, setLinkedItem] = useState<string | null>(null);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const retailItemsSource = useMemo(() => {
    if (spaRetailItems != null) {
      return spaRetailItems;
    }
    if (fieldValues && !Array.isArray(fieldValues)) {
      return fieldValues[SpaPropertyEnum.retailItems] ?? [];
    }
    return [];
  }, [spaRetailItems, fieldValues]);

  const linkedRetailItemOptions = useMemo(
    () => buildRetailItemNumberOptions(retailItemsSource),
    [retailItemsSource],
  );

  const validateDayPackageFields = (): boolean => {
    if (!packageCode || packageCode.length === 0) {
      setAlertMessage("Package Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setPackageCodeFieldError(false);
    setPackageCodeFieldErrorMessage("");
    if (
      !validateString(
        packageCode,
        validationLength.packageCode,
        validationType.CODE,
      )
    ) {
      setPackageCodeFieldError(true);
      setPackageCodeFieldErrorMessage(validationMessages.packageCode);
      return false;
    }

    if (!packageDescription || packageDescription.length === 0) {
      setAlertMessage("Package Description is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setPackageDescriptionFieldError(false);
    setPackageDescriptionFieldErrorMessage("");
    if (
      !validateString(
        packageDescription,
        validationLength.packageDescription,
        validationType.NAME,
      )
    ) {
      setPackageDescriptionFieldError(true);
      setPackageDescriptionFieldErrorMessage(
        validationMessages.packageDescription,
      );
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(listForDuplicate, DNDFieldsEnum.PACKAGECODE, packageCode)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (fieldValues) {
      setItems(
        Array.isArray(fieldValues)
          ? fieldValues
          : (fieldValues[SpaPropertyEnum.dayPackages] ?? []),
      );
    }
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateDayPackageFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.dayPackages} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateDayPackageFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setPackageCode(list["Package Code"] ?? null);
      setPackageDescription(list["Package Description"] ?? null);
      setEffectiveFromDate(
        list["Effective From Date"] ? dayjs(list["Effective From Date"]) : null,
      );
      setEffectiveToDate(
        list["Effective To Date"] ? dayjs(list["Effective To Date"]) : null,
      );
      setPrice(
        list?.Price !== undefined && list?.Price !== null
          ? Number(list.Price)
          : "",
      );
      setAvailableSun(list["Available Sun"] ?? "Yes");
      setAvailableMon(list["Available Mon"] ?? "Yes");
      setAvailableTue(list["Available Tue"] ?? "Yes");
      setAvailableWed(list["Available Wed"] ?? "Yes");
      setAvailableThu(list["Available Thu"] ?? "Yes");
      setAvailableFri(list["Available Fri"] ?? "Yes");
      setAvailableSat(list["Available Sat"] ?? "Yes");
      setMinimumGuests(
        list["Minimum Guests"] !== undefined && list["Minimum Guests"] !== null
          ? Number(list["Minimum Guests"])
          : "",
      );
      setMaximumGuests(
        list["Maximum Guests"] !== undefined && list["Maximum Guests"] !== null
          ? Number(list["Maximum Guests"])
          : "",
      );
      setPoints(
        list?.Points !== undefined && list?.Points !== null
          ? Number(list.Points)
          : "",
      );
      setActive(list?.Active ?? "Yes");
      setLinkedItem(
        list["Linked Item"] != null && list["Linked Item"] !== ""
          ? String(list["Linked Item"])
          : String(getDefaultRetailItemNumber()),
      );
    } else {
      setPackageCode(null);
      setPackageDescription(null);
      setPackageCodeFieldError(false);
      setPackageCodeFieldErrorMessage("");
      setPackageDescriptionFieldError(false);
      setPackageDescriptionFieldErrorMessage("");
      setEffectiveFromDate(null);
      setEffectiveToDate(null);
      setPrice("");
      setAvailableSun("Yes");
      setAvailableMon("Yes");
      setAvailableTue("Yes");
      setAvailableWed("Yes");
      setAvailableThu("Yes");
      setAvailableFri("Yes");
      setAvailableSat("Yes");
      setMinimumGuests("");
      setMaximumGuests("");
      setPoints("");
      setActive("Yes");
      setLinkedItem(String(getDefaultRetailItemNumber()));
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Package Code": packageCode,
    "Package Description": packageDescription,
    "Effective From Date": effectiveFromDate
      ? effectiveFromDate.format("YYYY-MM-DD")
      : undefined,
    "Effective To Date": effectiveToDate
      ? effectiveToDate.format("YYYY-MM-DD")
      : undefined,
    Price: price === "" ? undefined : Number(price),
    "Available Sun": availableSun,
    "Available Mon": availableMon,
    "Available Tue": availableTue,
    "Available Wed": availableWed,
    "Available Thu": availableThu,
    "Available Fri": availableFri,
    "Available Sat": availableSat,
    "Minimum Guests": minimumGuests === "" ? undefined : Number(minimumGuests),
    "Maximum Guests": maximumGuests === "" ? undefined : Number(maximumGuests),
    Points: points === "" ? undefined : Number(points),
    Active: active,
    "Linked Item": linkedItem,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Packages
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.dayPackageHeader ?? "Packages",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack
            className="form-two-col-row"
            direction="row"
            flexWrap="wrap"
            gap="16px"
          >
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Package Code *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.dayPackagePackageCode,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                variant="outlined"
                size="small"
                value={packageCode ?? ""}
                onChange={(e) => setPackageCode(e.target.value || null)}
                fullWidth
                helperText={
                  packageCodeFieldError ? packageCodeFieldErrorMessage : ""
                }
                error={packageCodeFieldError}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Package Description *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.dayPackagePackageDescription,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                variant="outlined"
                size="small"
                value={packageDescription ?? ""}
                onChange={(e) => setPackageDescription(e.target.value || null)}
                error={packageDescriptionFieldError}
                helperText={
                  packageDescriptionFieldError
                    ? packageDescriptionFieldErrorMessage
                    : ""
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Effective From Date *</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.dayPackageStartDate,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <DatePicker
                value={effectiveFromDate}
                onChange={(v) => setEffectiveFromDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Effective To Date</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.dayPackageEndDate)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <DatePicker
                value={effectiveToDate}
                onChange={(v) => setEffectiveToDate(v)}
                slotProps={{ textField: { size: "small", fullWidth: true } }}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Price</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.dayPackagePrice)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                inputProps={{ step: 0.01 }}
                variant="outlined"
                size="small"
                value={price}
                onChange={(e) =>
                  setPrice(
                    e.target.value === ""
                      ? ""
                      : parseFloat(e.target.value) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Min Guests</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceMinimumGuests,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={minimumGuests}
                onChange={(e) =>
                  setMinimumGuests(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Max Guests</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceMaximumGuests,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={maximumGuests}
                onChange={(e) =>
                  setMaximumGuests(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Points</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.serviceServicePoints,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <TextField
                type="number"
                variant="outlined"
                size="small"
                value={points}
                onChange={(e) =>
                  setPoints(
                    e.target.value === ""
                      ? ""
                      : parseInt(e.target.value, 10) || "",
                  )
                }
                fullWidth
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Linked Retail Item</FormLabel>
                <Tooltip
                  title={tooltipComponent(
                    spaTooltipHeaders.dayPackageLinkedItem,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={linkedRetailItemOptions}
                value={linkedRetailItemToOptionValue(
                  linkedItem,
                  linkedRetailItemOptions,
                )}
                disabled={!linkedRetailItemOptions.length}
                onChange={(_, v) =>
                  setLinkedItem(parseLinkedRetailItemFromOption(v))
                }
                isOptionEqualToValue={(a, b) => a === b}
                noOptionsText="No retail items available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={
                      linkedRetailItemOptions.length
                        ? SPA_SERVICES_LINKED_ITEM_PLACEHOLDER
                        : "10 - Retail Item"
                    }
                  />
                )}
              />
            </Stack>
            <Stack direction="column" className="form-two-col-field">
              <Stack direction="row">
                <FormLabel>Active</FormLabel>
                <Tooltip
                  title={tooltipComponent(spaTooltipHeaders.dayPackageActive)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Autocomplete
                size="small"
                options={YES_NO}
                value={active}
                onChange={(_, v) => setActive(v ?? "Yes")}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" />
                )}
              />
            </Stack>
            <Stack
              direction="column"
              className="form-full-width-field"
              gap="12px"
            >
              <Stack direction="row" alignItems="center">
                <FormLabel component="legend">Available Days</FormLabel>
              </Stack>
              <Stack
                className="form-two-col-row"
                direction="row"
                flexWrap="wrap"
                gap="12px"
              >
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Sun</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableSun,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableSun}
                    onChange={(_, v) => setAvailableSun(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Mon</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableMon,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableMon}
                    onChange={(_, v) => setAvailableMon(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Tue</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableTue,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableTue}
                    onChange={(_, v) => setAvailableTue(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Wed</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableWed,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableWed}
                    onChange={(_, v) => setAvailableWed(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Thu</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableThu,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableThu}
                    onChange={(_, v) => setAvailableThu(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Fri</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableFri,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableFri}
                    onChange={(_, v) => setAvailableFri(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
                <Stack direction="column" className="form-two-col-field">
                  <Stack direction="row">
                    <FormLabel>Sat</FormLabel>
                    <Tooltip
                      title={tooltipComponent(
                        spaTooltipHeaders.dayPackageAvailableSat,
                      )}
                    >
                      <InfoIcon fontSize="small" />
                    </Tooltip>
                  </Stack>
                  <Autocomplete
                    size="small"
                    options={YES_NO}
                    value={availableSat}
                    onChange={(_, v) => setAvailableSat(v ?? "Yes")}
                    renderInput={(params) => (
                      <TextField {...params} variant="outlined" />
                    )}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </LocalizationProvider>
        <Stack direction="row" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Package
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Packages Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.PACKAGECODE}
              name={DNDFieldsEnum.PACKAGEDESCRIPTION}
            />
          ) : (
            <Typography variant="body2">No Packages Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaDayPackages;
